import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { getHeaders } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface Employee {
  employeeId?: string;
  firstName?: string;
  lastName?: string;
  [key: string]: any;
}

interface EmployeeType {
  id?: string;
  typeName?: string;
}

interface Props {
  propertyId: string | undefined;
  currentIntegration?: any | null;
  employeeTypes?: EmployeeType[];
}

const EmployeesCard: React.FC<Props> = ({ propertyId, currentIntegration, employeeTypes }) => {
  const { toast } = useToast();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchEmployeesFromPayroll = async () => {
    if (!currentIntegration) {
      // don't auto-toast on mount when integration is not configured
      return;
    }

    try {
      setLoading(true);
      const baseHeaders = await getHeaders();
      const headers = { ...baseHeaders, "Content-Type": "application/json" } as HeadersInit;

      const integrationId = currentIntegration.integrationId || currentIntegration.id || null;
      const payrollProvider = (currentIntegration?.name || currentIntegration?.provider || "").toString();
      const body = JSON.stringify({ integrationId, propertyId, payrollProvider });

      const resp = await fetch(`${import.meta.env.VITE_API_URL}/payroll/employees`, {
        method: "POST",
        mode: "cors",
        headers,
        body,
      });

      if (!resp.ok) {
        throw new Error(`Failed to fetch employees: ${resp.status}`);
      }

      const data = await resp.json();
      let list: Employee[] = [];
      if (Array.isArray(data)) list = data;
      else if (data && data.employees) list = data.employees;
      else if (data && data.data) list = data.data;
      else if (data && data.body) list = data.body;

      setEmployees(list || []);
      toast({ title: "Employees loaded" });
    } catch (error) {
      console.error("Error fetching employees", error);
      toast({ title: "Error fetching employees", description: String(error), variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const updateEmployeeType = async (employee: Employee, selectedTypeId: string) => {
    if (!currentIntegration) {
      toast({ title: "No integration", description: "Connect an accounting integration before updating employees.", variant: "destructive" });
      return;
    }

    const integrationId = currentIntegration.integrationId || currentIntegration.id || null;
    const payrollProvider = (currentIntegration?.name || currentIntegration?.provider || "").toString();

    const selectedType = (employeeTypes || []).find((t) => (t.id || t.typeName) === selectedTypeId) || null;
    const payload = {
      propertyId,
      integrationId,
      payrollProvider,
      employeeId: employee.employeeId || employee.id,
      employeeTypeId: selectedType ? selectedType.id : selectedTypeId || null,
      employeeTypeName: selectedType ? selectedType.typeName : null,
    };

    try {
      const baseHeaders = await getHeaders();
      const headers = { ...baseHeaders, "Content-Type": "application/json" } as HeadersInit;

      const resp = await fetch(`${import.meta.env.VITE_API_URL}/payroll/employees`, {
        method: "PUT",
        mode: "cors",
        headers,
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        throw new Error(`Failed to update employee: ${resp.status}`);
      }

      // Update local state to reflect the new selection
      setEmployees((prev) =>
        prev.map((e) => {
          if ((e.employeeId || e.id) === (employee.employeeId || employee.id)) {
            return { ...e, employeeTypeId: payload.employeeTypeId, employeeTypeName: payload.employeeTypeName };
          }
          return e;
        })
      );

      toast({ title: "Saved", description: "Employee type updated." });
    } catch (err) {
      console.error("Error updating employee type", err);
      toast({ title: "Error", description: "Failed to update employee type.", variant: "destructive" });
    }
  };

  // Fetch saved employees from our DB for this property
  const fetchEmployeesFromDB = async () => {
    if (!propertyId) return;
    try {
      setLoading(true);
      const headers = await getHeaders();
      const url = `${import.meta.env.VITE_API_URL}/payroll/employees/db?propertyId=${propertyId}`;
      const resp = await fetch(url, { method: "GET", headers });
      if (!resp.ok) throw new Error(`Failed to fetch employees from DB: ${resp.status}`);
      const data = await resp.json();
      let list: Employee[] = [];
      if (Array.isArray(data)) list = data;
      else if (data && data.employees) list = data.employees;
      else if (data && data.data) list = data.data;
      else if (data && data.body) list = data.body;

      setEmployees(list || []);
      // no toast to avoid noisy messages on load
    } catch (error) {
      console.error("Error fetching employees from DB", error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch employees when the card loads and when integration/property changes
  useEffect(() => {

    if (currentIntegration) {
          // Always load saved employees from DB on load / when property changes
          fetchEmployeesFromDB();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIntegration, propertyId]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Employees</CardTitle>
        <CardDescription>Retrieve payroll employees for the connected accounting integration</CardDescription>
      </CardHeader>

      <CardContent>
        <div className="flex items-center justify-between mb-3">
          <div className="flex space-x-2">
            <Button size="sm" onClick={fetchEmployeesFromPayroll} disabled={loading}>{loading ? 'Loading...' : 'Get Employees'}</Button>
          </div>
        </div>

        {employees.length === 0 ? (
          <p className="text-sm text-muted-foreground">No employees loaded.</p>
        ) : (
          <div className="space-y-2">
            <ul className="list-disc pl-5">
              {employees.map((e, idx) => {
                const selected = (e.employeeTypeId || e.employeeType || e.employeeTypeName || e.employee_type || "") as string;
                return (
                  <li key={(e.employeeId || e.id || idx).toString()} className="text-sm">
                    <div className="font-medium">{e.firstName || e.first_name || ""} {e.lastName || e.last_name || ""}</div>
                    <div className="mt-1">
                      <select
                        value={selected || ""}
                        onChange={(ev) => updateEmployeeType(e, ev.target.value)}
                        className="mt-1 block w-full rounded-md border px-2 py-1 text-sm"
                      >
                        <option value="">— Select type —</option>
                        {(employeeTypes || []).map((t) => (
                          <option key={(t.id || t.typeName) as string} value={(t.id || t.typeName) as string}>
                            {t.typeName}
                          </option>
                        ))}
                      </select>
                    </div>
                    {/* <div className="text-sm text-muted-foreground">Employee ID: {e.employeeId || e.id || "-"}</div> */}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EmployeesCard;
